import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { cpf } from 'cpf-cnpj-validator';
import classNames from 'classnames';

import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { InputMask } from 'primereact/inputmask';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';

import LoadingScreen from '../../components/LoadingScreen';
import { useAuth } from 'src/contexts/auth';

import { useFormCreateUnityPeoples } from 'src/controllers/People/CreateUnityPeopleController';
import { useListUnitysController } from 'src/controllers/Unitys/ListUnitysController';
import { birthValidation } from 'src/utils/birthValidation';

interface filteredProps {
	label: string;
	value?: string;
}

const UnityPeopleForm: React.FC = () => {
	const { getUnityPeopleFormController, handleUnityPeopleFormController } = useFormCreateUnityPeoples();
	const { getUnityListController } = useListUnitysController();
	const { selectedPeopleUnity } = useAuth();

	const [valueUnity, setValueUnity] = useState('');
	const [filteredUnity, setFilteredUnity] = useState<filteredProps[]>([{ label: '', value: '' }]);
	const [valueRole, setValueRole] = useState('');
	const [filteredRole, setFilteredRole] = useState<filteredProps[]>([{ label: '', value: '' }]);

	const [registerFinished, setRegisterFinished] = useState(false);

	const history = useHistory();
	const checkCpfCnpj = cpf.isValid(getUnityPeopleFormController.formik.values.CPF || '');
	const isValidDate = birthValidation(getUnityPeopleFormController.formik.values.DATANASCIMENTO);

	useEffect(() => {
		if (selectedPeopleUnity) {
			getUnityPeopleFormController.formik.setFieldValue('ORGAOUNIDADE', selectedPeopleUnity.ID);
			setValueUnity(selectedPeopleUnity.NOME);
		}
	}, []);

	useEffect(() => {
		if (getUnityPeopleFormController.peopleResponse?.status === 200) {
			setRegisterFinished(true);
		}
		if (
			getUnityPeopleFormController.peopleEditedResponse?.status === 200 &&
			!getUnityPeopleFormController.peopleResponse
		) {
			setRegisterFinished(false);
			setValueUnity('');
			setValueRole('');
		}
	}, [getUnityPeopleFormController.peopleResponse, getUnityPeopleFormController.peopleEditedResponse]);

	const unitys = getUnityListController.UnitysData.map(unity => {
		return { label: unity.NOME, value: unity.ID };
	});

	const roles = getUnityPeopleFormController.roles.map(role => {
		return { label: role.DESCRICAO, value: role.ID };
	});

	const searchUnitys = (event: any) => {
		let filteredUnitys;
		if (!event.query.trim().length) {
			filteredUnitys = [...unitys];
		} else {
			filteredUnitys = unitys.filter(unity => {
				return unity.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}
		setFilteredUnity(filteredUnitys);
	};

	const searchRoles = (event: any) => {
		let filteredRoles;
		if (!event.query.trim().length) {
			filteredRoles = [...roles];
		} else {
			filteredRoles = roles.filter(role => {
				return role.label.toLowerCase().includes(event.query.toLowerCase());
			});
		}
		setFilteredRole(filteredRoles);
	};

	return (
		<div style={{ height: '100%' }} className='card p-align-center p-justify-center'>
			<Toast ref={getUnityPeopleFormController.toast} />
			<h5>Cadastro de responsável por unidade</h5>
			{getUnityPeopleFormController.loading ? <LoadingScreen color='#0388e5' /> : <></>}
			{getUnityPeopleFormController.loadingPersonCpf ? <LoadingScreen color='#0388e5' /> : <></>}
			<div className='p-grid p-fluid'>
				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='cpf'>CPF</label>
					<InputMask
						id='cpf'
						autoClear={false}
						mask='999.999.999-99'
						placeholder='___.___.___-__'
						{...getUnityPeopleFormController.formik.getFieldProps('CPF')}
						onChange={e => getUnityPeopleFormController.formik.setFieldValue('CPF', e.value)}
						onComplete={(e: any) => handleUnityPeopleFormController.searchPersonCpf(e.value)}
						disabled={getUnityPeopleFormController.foundPersonCpf || registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getUnityPeopleFormController.formik.touched.CPF &&
								(getUnityPeopleFormController.formik.errors.CPF || !checkCpfCnpj),
							'popup-form-inactive': !(
								getUnityPeopleFormController.formik.touched.CPF &&
								(getUnityPeopleFormController.formik.errors.CPF || !checkCpfCnpj)
							),
						})}>
						{!checkCpfCnpj ? '* Informe CPF válido' : getUnityPeopleFormController.formik.errors.CPF}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-8'>
					<label htmlFor='nome'>Nome</label>
					<InputText
						disabled={registerFinished}
						id='nome'
						maxLength={150}
						{...getUnityPeopleFormController.formik.getFieldProps('NOME')}
						onChange={getUnityPeopleFormController.formik.handleChange('NOME')}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getUnityPeopleFormController.formik.touched.NOME &&
								getUnityPeopleFormController.formik.errors.NOME,
							'popup-form-inactive': !(
								getUnityPeopleFormController.formik.touched.NOME &&
								getUnityPeopleFormController.formik.errors.NOME
							),
						})}>
						{getUnityPeopleFormController.formik.errors.NOME}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-2'>
					<label htmlFor='dataNasc'>Data de nascimento</label>
					<InputMask
						disabled={registerFinished}
						mask='99/99/9999'
						placeholder='__/__/____'
						{...getUnityPeopleFormController.formik.getFieldProps('DATANASCIMENTO')}
						onChange={e => getUnityPeopleFormController.formik.setFieldValue('DATANASCIMENTO', e.value)}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getUnityPeopleFormController.formik.touched.DATANASCIMENTO &&
								(getUnityPeopleFormController.formik.errors.DATANASCIMENTO || !isValidDate),
							'popup-form-inactive': !(
								getUnityPeopleFormController.formik.touched.DATANASCIMENTO &&
								(getUnityPeopleFormController.formik.errors.DATANASCIMENTO || !isValidDate)
							),
						})}>
						{!isValidDate
							? '* Deve ser maior de 18 anos'
							: getUnityPeopleFormController.formik.errors.DATANASCIMENTO}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-4'>
					<label htmlFor='cargo'>Cargo</label>
					<AutoComplete
						id='cargo'
						value={valueRole}
						field='label'
						placeholder='Buscar...'
						onChange={e => {
							setValueRole(e.value);
							getUnityPeopleFormController.formik.setFieldValue(
								'CARGO',
								filteredRole.find(role => {
									return e.value === role.label;
								})?.value || '',
							);
						}}
						onSelect={e => {
							setValueRole(e.value.label);
							getUnityPeopleFormController.formik.setFieldValue('CARGO', e.value.value);
						}}
						suggestions={filteredRole}
						completeMethod={searchRoles}
						dropdown
						disabled={registerFinished}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getUnityPeopleFormController.formik.touched.CARGO &&
								getUnityPeopleFormController.formik.errors.CARGO,
							'popup-form-inactive': !(
								getUnityPeopleFormController.formik.touched.CARGO &&
								getUnityPeopleFormController.formik.errors.CARGO
							),
						})}>
						{getUnityPeopleFormController.formik.errors.CARGO}
					</div>
				</div>

				<div className='p-field p-col-12 p-md-8'>
					<label htmlFor='unidade'>Unidade</label>
					<AutoComplete
						id='unidade'
						value={valueUnity}
						disabled={registerFinished}
						field='label'
						placeholder='Buscar...'
						onChange={e => {
							setValueUnity(e.value);
							getUnityPeopleFormController.formik.setFieldValue(
								'ORGAOUNIDADE',
								filteredUnity.find(unity => {
									return valueUnity.toLowerCase() === unity.label.toLowerCase();
								})?.value || '',
							);
						}}
						onSelect={e => {
							setValueUnity(e.value.label);
							getUnityPeopleFormController.formik.setFieldValue('ORGAOUNIDADE', e.value.value);
						}}
						suggestions={filteredUnity}
						completeMethod={searchUnitys}
						dropdown
						dropdownMode='current'
						minLength={2}
						delay={0}
					/>
					<div
						className={classNames('p-invalid popup-fade', {
							'popup-form-active':
								getUnityPeopleFormController.formik.touched.ORGAOUNIDADE &&
								getUnityPeopleFormController.formik.errors.ORGAOUNIDADE,
							'popup-form-inactive': !(
								getUnityPeopleFormController.formik.touched.ORGAOUNIDADE &&
								getUnityPeopleFormController.formik.errors.ORGAOUNIDADE
							),
						})}>
						{getUnityPeopleFormController.formik.errors.ORGAOUNIDADE}
					</div>
				</div>
			</div>

			<div className='p-fluid p-grid p-justify-between'>
				{getUnityPeopleFormController.peopleResponse?.status === 200 && !registerFinished ? (
					<div className='p-col-12 p-md-4'>
						<Button
							type='button'
							label='Salvar Alterações'
							onClick={() =>
								checkCpfCnpj && isValidDate ? getUnityPeopleFormController.formik.handleSubmit() : {}
							}
						/>
					</div>
				) : getUnityPeopleFormController.peopleResponse?.status === 200 && registerFinished ? (
					<div className='p-col-12 p-md-6 p-fluid p-grid'>
						<div className='p-col-12 p-md-5'>
							<Button
								type='button'
								label='Editar'
								onClick={() => {
									setRegisterFinished(false);
								}}
							/>
						</div>
						<div className='p-col-12 p-md-5'>
							<Button
								icon='pi pi-plus'
								type='button'
								className='p-button-success'
								label='Cadastrar novo'
								onClick={() => {
									getUnityPeopleFormController.formik.resetForm();
									setValueUnity('');
									setValueRole('');
									setRegisterFinished(false);
									handleUnityPeopleFormController.setPeopleResponse(null);
								}}
							/>
						</div>
					</div>
				) : (
					<div className='p-col-12 p-md-4'>
						<Button
							type='button'
							label='Cadastrar'
							onClick={() =>
								checkCpfCnpj && isValidDate ? getUnityPeopleFormController.formik.handleSubmit() : {}
							}
						/>
					</div>
				)}

				<div className='p-col-12 p-md-4'>
					<Button
						type='button'
						className='p-button-secondary'
						label='Voltar para listagem'
						onClick={() => history.goBack()}
					/>
				</div>
			</div>
		</div>
	);
};

export default UnityPeopleForm;
